import { useState, useRef, useEffect, useMemo, memo } from 'react'
import { isMobile } from 'react-device-detect'
import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import CategoryNavScroll from './CategoryNavScroll'
import { MenuCategory } from '@open-tender/types'
import CategoryPopupMenu from './CategoryPopupMenu'

const CategoryNavView = styled.div`
  label: CategoryNav;
  width: 100%;
  height: ${(props) => props.theme.layout.navHeight};
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    height: ${(props) => props.theme.layout.navHeightMobile};
  }
`

interface CategoryNavInnerProps {
  stuck?: boolean
}

const CategoryNavInner = styled.div<CategoryNavInnerProps>`
  width: 100%;
  background-color: ${(props) => props.theme.bgColors.dark};
  ${(props) =>
    props.stuck &&
    `position: fixed;
      z-index: 10;
      top: ${props.theme.layout.navHeight};
      left: 0;
      right: 0;`}
  @media (max-width: ${(props) => props.theme.breakpoints.tablet}) {
    ${(props) =>
      props.stuck &&
      `position: fixed;
      z-index: 10;
      top: ${props.theme.layout.navHeightMobile};
      left: 0;
      right: 0;`}
  }
`

const CategoryNavContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  @media (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: flex-start;
  }
`

interface CategoryNavProps {
  items: string[]
  categories?: MenuCategory[]
  offset?: number
  setActiveSection?: (name: string) => void
  showSubCategory?: boolean
}

const CategoryNav = memo(({
  items,
  categories,
  offset = -30,
  setActiveSection,
  showSubCategory,
}: CategoryNavProps) => {
  const [stuck, setStuck] = useState(false)
  const stickyRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()
  const { navHeight, navHeightMobile } = theme.layout
  const height = isMobile ? navHeightMobile : navHeight
  const heightInPixels = useMemo(() => parseInt(height.replace('rem', '')) * 10, [height])
  const topOffset = useMemo(() => heightInPixels, [heightInPixels])

  useEffect(() => {
    const handleScroll = () => {
      if (stickyRef.current) {
        setStuck(stickyRef.current.getBoundingClientRect().top <= topOffset)
      }
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [topOffset])

  return (
    <CategoryNavView ref={stickyRef}>
      <CategoryNavInner stuck={stuck}>
        <CategoryNavContainer>
          {showSubCategory ? (
            <CategoryPopupMenu
              categories={categories as MenuCategory[]}
              setActiveSection={setActiveSection}
            />
          ) : (
            <CategoryNavScroll
              items={items}
              offset={offset}
              setActiveSection={setActiveSection}
            />
          )}
        </CategoryNavContainer>
      </CategoryNavInner>
    </CategoryNavView>
  )
})

export default CategoryNav
